import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, Upload, X } from 'lucide-react';
import { uploadClaimFile } from '../utils/claimForm';
import productexLogo from '../components/layout/productex-logo.png';

interface ClaimData {
  id: string;
  token: string;
  entity_name: string;
  entity_type: string;
  client_name: string | null;
  status: string;
}

interface ClaimQuestion {
  question_key: string;
  label: string;
  field_type: string;
  options: { value: string; label: string }[];
  is_required: boolean;
  conditional_on: { question_key: string; value: string } | null;
  min_files?: number;
}

const DECLARATION_TEXT = `Declaro bajo carácter de declaración jurada que toda la información brindada en este formulario es completa, veraz y verificable.
Acepto que el presente reclamo será evaluado exclusivamente en base a la información aquí proporcionada, la evidencia adjunta y las condiciones comerciales y contractuales vigentes con Productex.

Reconozco que:
- La presentación del reclamo no implica aceptación automática del mismo.
- Productex podrá rechazar total o parcialmente el reclamo si se detectan inconsistencias, falta de evidencia, mal uso del producto o incumplimiento de los plazos contractuales.
- La resolución que eventualmente se proponga será definitiva una vez aceptada y ejecutada, no admitiendo reclamos posteriores por la misma muestra o producción.

En caso de que Productex decida aceptar el reclamo y brindar una solución, esta se dará dentro del plazo de las próximas 96 hs. hábiles una vez recibido el formulario completo.`;

const PageShell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800">
    <header className="bg-black text-white px-6 py-4 flex items-center justify-center">
      <img
        src={productexLogo}
        alt="Productex"
        className="h-8 w-auto object-contain"
      />
    </header>
    {children}
  </div>
);

const FormField = ({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-900 dark:text-white">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const ClaimForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [claim, setClaim] = useState<ClaimData | null>(null);
  const [questions, setQuestions] = useState<ClaimQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [within20Days, setWithin20Days] = useState('');
  const [claimType, setClaimType] = useState('');
  const [claimTypeOther, setClaimTypeOther] = useState('');
  const [unitsAffected, setUnitsAffected] = useState('');
  const [description, setDescription] = useState('');
  const [whenDetected, setWhenDetected] = useState('');
  const [daysAfterDelivery, setDaysAfterDelivery] = useState('');
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [washedUsed, setWashedUsed] = useState('');
  const [washDetail, setWashDetail] = useState('');
  const [affectsCommercialization, setAffectsCommercialization] = useState('');
  const [expectedSolution, setExpectedSolution] = useState('');
  const [expectedSolutionOther, setExpectedSolutionOther] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const photoPreviews = useMemo(
    () => photoFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [photoFiles]
  );

  useEffect(() => {
    return () => {
      photoPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [photoPreviews]);

  useEffect(() => {
    if (token) loadData();
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [claimRes, questionsRes] = await Promise.all([
        supabase.from('claims').select('id, token, entity_name, entity_type, client_name, status').eq('token', token).maybeSingle(),
        supabase.from('claim_form_questions').select('question_key, label, field_type, options, is_required, conditional_on, min_files').order('sort_order'),
      ]);
      if (claimRes.error) throw claimRes.error;
      if (questionsRes.error) throw questionsRes.error;
      if (!claimRes.data) {
        setError('Reclamo no encontrado');
        setLoading(false);
        return;
      }
      if (claimRes.data.status === 'completed') setSubmitted(true);
      setClaim(claimRes.data as ClaimData);
      setQuestions((questionsRes.data || []).map((q: any) => ({
        ...q,
        options: Array.isArray(q.options) ? q.options : [],
      })));
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  const getLabel = (key: string) => questions.find((q) => q.question_key === key)?.label || key;
  const getOptions = (key: string) => questions.find((q) => q.question_key === key)?.options || [];

  const showConditional = (cond: { question_key: string; value: string } | null) => {
    if (!cond) return true;
    const val = { washed_used: washedUsed }[cond.question_key];
    return val === cond.value;
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    setPhotoFiles((prev) => [...prev, ...imageFiles].slice(0, 20));
    // Permite volver a seleccionar los mismos archivos (iOS/Android a veces no dispara onChange si es el mismo)
    e.target.value = '';
  };

  const removePhoto = (idx: number) => {
    if (idx < photoUrls.length) {
      setPhotoUrls((prev) => prev.filter((_, i) => i !== idx));
    } else {
      setPhotoFiles((prev) => prev.filter((_, i) => i !== idx - photoUrls.length));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setError(null);

    try {
      let urls = [...photoUrls];
      if (photoFiles.length > 0) {
        setUploadingPhotos(true);
        for (const file of photoFiles) {
          const url = await uploadClaimFile(token, file, 'photo');
          if (url) urls.push(url);
        }
        setUploadingPhotos(false);
      }

      if (urls.length < 2) {
        throw new Error('Se requieren al menos 2 fotos del problema');
      }

      const { data, error: err } = await supabase.rpc('submit_claim', {
        p_token: token,
        p_contact_name: contactName.trim(),
        p_contact_email: contactEmail.trim(),
        p_contact_phone: contactPhone.trim(),
        p_delivery_date: deliveryDate || null,
        p_within_20_days: within20Days || null,
        p_claim_type: claimType,
        p_claim_type_other: claimTypeOther || null,
        p_units_affected: unitsAffected,
        p_description: description.trim(),
        p_when_detected: whenDetected,
        p_days_after_delivery: daysAfterDelivery ? parseInt(daysAfterDelivery, 10) : null,
        p_photo_urls: urls,
        p_video_url: videoUrl.trim() || null,
        p_washed_used: washedUsed,
        p_wash_detail: washDetail.trim() || null,
        p_affects_commercialization: affectsCommercialization,
        p_expected_solution: expectedSolution,
        p_expected_solution_other: expectedSolutionOther || null,
        p_accept_terms: acceptTerms,
      });

      const result = data as { success?: boolean; error?: string };
      if (err || !result?.success) {
        throw new Error(result?.error || err?.message || 'Error al enviar');
      }
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message || 'Error al enviar el reclamo');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-indigo-500 border-t-transparent" />
          <p className="ml-4 text-gray-600 dark:text-gray-400">Cargando formulario...</p>
        </div>
      </PageShell>
    );
  }

  if (error && !claim) {
    return (
      <PageShell>
        <div className="flex justify-center p-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
            <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell>
        <div className="flex justify-center p-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Reclamo enviado</h1>
            <p className="text-gray-600 dark:text-gray-400">Recibirás respuesta en las próximas 96 hs hábiles.</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!claim) return null;

  const canSubmit =
    contactName.trim() &&
    contactEmail.trim() &&
    contactPhone.trim() &&
    deliveryDate &&
    claimType &&
    unitsAffected &&
    description &&
    whenDetected &&
    washedUsed &&
    affectsCommercialization &&
    expectedSolution &&
    acceptTerms &&
    (photoUrls.length + photoFiles.length) >= 2 &&
    (washedUsed !== 'Sí' || washDetail.trim());

  const inputClass =
    'w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500';

  return (
    <PageShell>
      <div className="py-12 px-4 max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <p className="font-medium text-gray-900 dark:text-white">
              {claim.entity_name} {claim.client_name && `· ${claim.client_name}`}
            </p>
          </div>

          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Formulario de reclamo</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <FormField label={getLabel('contact_name')} required>
              <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} className={inputClass} placeholder="Nombre y apellido" required />
            </FormField>

            <FormField label={getLabel('contact_email')} required>
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={inputClass} placeholder="email@ejemplo.com" required />
            </FormField>

            <FormField label={getLabel('contact_phone')} required>
              <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className={inputClass} placeholder="+54 9 11 1234-5678" required />
            </FormField>

            <FormField label={getLabel('delivery_date')} required>
              <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className={inputClass} required />
            </FormField>

            <FormField label={getLabel('within_20_days')}>
              <select value={within20Days} onChange={(e) => setWithin20Days(e.target.value)} className={inputClass}>
                <option value="">Seleccionar</option>
                {getOptions('within_20_days').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={getLabel('claim_type')} required>
              <select value={claimType} onChange={(e) => setClaimType(e.target.value)} className={inputClass} required>
                <option value="">Seleccionar</option>
                {getOptions('claim_type').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {claimType === 'otro' && (
                <input
                  type="text"
                  value={claimTypeOther}
                  onChange={(e) => setClaimTypeOther(e.target.value)}
                  placeholder="Especificar"
                  className={`${inputClass} mt-2`}
                />
              )}
            </FormField>

            <FormField label={getLabel('units_affected')} required>
              <select value={unitsAffected} onChange={(e) => setUnitsAffected(e.target.value)} className={inputClass} required>
                <option value="">Seleccionar</option>
                {getOptions('units_affected').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={getLabel('description')} required>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className={inputClass} placeholder="Descripción clara del problema" required />
            </FormField>

            <FormField label={getLabel('when_detected')} required>
              <select value={whenDetected} onChange={(e) => setWhenDetected(e.target.value)} className={inputClass} required>
                <option value="">Seleccionar</option>
                {getOptions('when_detected').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={getLabel('days_after_delivery')}>
              <input
                type="number"
                min={0}
                value={daysAfterDelivery}
                onChange={(e) => setDaysAfterDelivery(e.target.value)}
                className={inputClass}
                placeholder="Días"
              />
            </FormField>

            <FormField label={getLabel('photos')} required>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Mínimo 2 imágenes obligatorias</p>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="w-full py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Adjuntar imágenes
              </button>
              <div className="flex flex-wrap gap-2 mt-2">
                {photoUrls.map((url, i) => (
                  <div key={`url-${i}`} className="relative">
                    <img src={url} alt="" className="h-20 w-20 object-cover rounded-lg border" />
                    <button type="button" onClick={() => removePhoto(i)} className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {photoPreviews.map((p, i) => (
                  <div key={`file-${i}`} className="relative">
                    <img
                      src={p.url}
                      alt=""
                      className="h-20 w-20 object-cover rounded-lg border"
                    />
                    <button type="button" onClick={() => removePhoto(photoUrls.length + i)} className="p-0.5 text-red-500 hover:bg-red-100 rounded">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </FormField>

            <FormField label={getLabel('video')}>
              <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} className={inputClass} placeholder="URL del video (opcional)" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">O subí un archivo de video</p>
              <input type="file" accept="video/*" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file && token) {
                  const url = await uploadClaimFile(token, file, 'video');
                  if (url) setVideoUrl(url);
                }
              }} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-100 mt-1" />
            </FormField>

            <FormField label={getLabel('washed_used')} required>
              <select value={washedUsed} onChange={(e) => setWashedUsed(e.target.value)} className={inputClass} required>
                <option value="">Seleccionar</option>
                {getOptions('washed_used').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>

            {showConditional({ question_key: 'washed_used', value: 'Sí' }) && washedUsed === 'Sí' && (
              <FormField label={getLabel('wash_detail')} required>
                <textarea value={washDetail} onChange={(e) => setWashDetail(e.target.value)} rows={3} className={inputClass} placeholder="Detallar uso y/o lavado" required />
              </FormField>
            )}

            <FormField label={getLabel('affects_commercialization')} required>
              <select value={affectsCommercialization} onChange={(e) => setAffectsCommercialization(e.target.value)} className={inputClass} required>
                <option value="">Seleccionar</option>
                {getOptions('affects_commercialization').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={getLabel('expected_solution')} required>
              <select value={expectedSolution} onChange={(e) => setExpectedSolution(e.target.value)} className={inputClass} required>
                <option value="">Seleccionar</option>
                {getOptions('expected_solution').map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {expectedSolution === 'otra' && (
                <input
                  type="text"
                  value={expectedSolutionOther}
                  onChange={(e) => setExpectedSolutionOther(e.target.value)}
                  placeholder="Especificar"
                  className={`${inputClass} mt-2`}
                />
              )}
            </FormField>

            <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line border border-gray-200 dark:border-gray-700">
              {DECLARATION_TEXT}
            </div>

            <FormField label={getLabel('accept_terms')} required>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 rounded border-gray-300 dark:border-gray-600"
                />
                <span className="text-sm">Acepto expresamente todas las condiciones anteriores.</span>
              </label>
            </FormField>

            <button
              type="submit"
              disabled={!canSubmit || submitting || uploadingPhotos}
              className="w-full py-4 rounded-xl bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {uploadingPhotos && <Upload className="h-5 w-5 animate-pulse" />}
              {submitting ? 'Enviando...' : 'Enviar reclamo'}
            </button>
          </form>
        </div>
      </div>
    </PageShell>
  );
};

export default ClaimForm;
